import { serializeError } from 'common/helpers'

import type { Query, QueryClient, FetchQueryOptions, QueryKey } from '@tanstack/react-query'

const QUERY_PREFETCH_SOURCE = 'home-prefetch'

const shouldRefetchScreenQueryOnMount = (query: Query) => query.state.status === 'error'

const SCREEN_QUERY_DEFAULTS = {
  retry: false,
  refetchOnMount: shouldRefetchScreenQueryOnMount,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
}

const PREFETCH_RETRY_ATTEMPTS = 3

interface PrefetchRequestDescriptor {
  queryKey: QueryKey
  evtId?: string
  endpoint: string
  method: string
  body?: unknown
  headers?: Record<string, unknown>
}

interface FailedPrefetchRequest extends PrefetchRequestDescriptor {
  errorMessage: string
}

interface PrefetchableQueryOptions {
  queryKey: QueryKey
  queryFn?: unknown
  meta?: Record<string, unknown>
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const hasQueryKey = (value: Record<string, unknown>): value is { queryKey: unknown } =>
  'queryKey' in value && Array.isArray(value.queryKey)

const hasQueryFn = (value: Record<string, unknown>): value is { queryFn: unknown } =>
  'queryFn' in value && typeof value.queryFn === 'function'

const isFetchQueryOptions = (value: unknown): value is FetchQueryOptions =>
  isRecord(value) && hasQueryKey(value) && hasQueryFn(value)

const isHomePrefetchQuery = (meta: Record<string, unknown> | undefined) =>
  meta?.source === QUERY_PREFETCH_SOURCE

const withHomePrefetchMeta = <TMeta extends Record<string, unknown> | undefined>(meta?: TMeta) => ({
  ...meta,
  source: QUERY_PREFETCH_SOURCE,
})

const enrichHomePrefetchQueryOptions = <T extends PrefetchableQueryOptions>(
  queryOptions: T,
  request: Omit<PrefetchRequestDescriptor, 'queryKey'>,
): T => ({
  ...queryOptions,
  meta: withHomePrefetchMeta({
    ...queryOptions.meta,
    prefetchRequest: {
      queryKey: queryOptions.queryKey,
      ...request,
    },
  }),
})

const getPrefetchRequestDescriptor = (
  queryOptions: FetchQueryOptions,
): PrefetchRequestDescriptor | undefined => {
  const prefetchRequest = (
    queryOptions.meta as { prefetchRequest?: PrefetchRequestDescriptor } | undefined
  )?.prefetchRequest

  if (!prefetchRequest) return undefined

  return prefetchRequest
}

const collectFailedPrefetchRequests = (
  queryClient: QueryClient,
  queryOptionsList: unknown[],
): FailedPrefetchRequest[] =>
  queryOptionsList.flatMap((queryOption) => {
    if (!isFetchQueryOptions(queryOption)) return []

    const queryState = queryClient.getQueryState(queryOption.queryKey)
    if (queryState?.status !== 'error') return []

    const prefetchRequest = getPrefetchRequestDescriptor(queryOption)

    return [
      {
        queryKey: queryOption.queryKey,
        evtId: prefetchRequest?.evtId,
        endpoint: prefetchRequest?.endpoint ?? '',
        method: prefetchRequest?.method ?? '',
        body: prefetchRequest?.body,
        headers: prefetchRequest?.headers,
        errorMessage: serializeError(queryState.error),
      },
    ]
  })

const cancelHomePrefetchQueries = (queryClient: QueryClient) =>
  queryClient.cancelQueries({
    predicate: (query) => isHomePrefetchQuery(query.meta),
  })

const hasSettledQueryState = (queryClient: QueryClient, queryKey: QueryKey) => {
  const queryState = queryClient.getQueryState(queryKey)

  return queryState?.status === 'success' || queryState?.status === 'error'
}

export type { FailedPrefetchRequest, PrefetchRequestDescriptor, PrefetchableQueryOptions }
export {
  PREFETCH_RETRY_ATTEMPTS,
  QUERY_PREFETCH_SOURCE,
  SCREEN_QUERY_DEFAULTS,
  cancelHomePrefetchQueries,
  collectFailedPrefetchRequests,
  enrichHomePrefetchQueryOptions,
  hasSettledQueryState,
  isFetchQueryOptions,
  isHomePrefetchQuery,
  shouldRefetchScreenQueryOnMount,
  withHomePrefetchMeta,
}
